import React, { useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import PropTypes from 'prop-types';

import Select from '../reactSelect/select.component';
import { ALL_OPTION } from '../constants';
import TruncatableSelect from '../reactSelect/truncatableSelect.component';
import AppkitIcon from '../appkitIcon/appkitIcon.component';

export const baseCellEditorParams = {
  config: {
    closeOnSelect: true,
    filterOptions: true,
    openOnFocus: true,
    tabSelectsValue: true,
    inputProps: {
      className: `cell-editor-dropdown`,
    },
    onSelectResetsInput: false,
  },
  autoSaveEnabled: true,
};

const SelectEditorComponent = ({
  value,
  options,
  config,
  withAllOption,
  truncatable,
  withClearIcon,
  defaultEmpty,
  onChange,
  onKeyDown,
}) => {
  const handleClearClick = useCallback(() => {
    const emptyValue = typeof defaultEmpty !== 'undefined' ? defaultEmpty : '';
    onChange(emptyValue);
  }, [defaultEmpty, onChange]);

  return (
    <div className="cell-editor-select-wrapper" style={{ minWidth: '100px' }} onKeyDown={onKeyDown}>
      {truncatable ? (
        <TruncatableSelect
          removeSelected
          virtualized
          onChange={onChange}
          options={options}
          scrollMenuIntoView={false}
          withAll={withAllOption}
          value={value}
          className={'cell-editor-select-truncatable'}
          {...config}
        />
      ) : (
        <>
          <Select
            removeSelected
            virtualized
            onChange={onChange}
            options={options}
            scrollMenuIntoView={false}
            withAll={withAllOption}
            value={value}
            className={'cell-editor-select'}
            {...config}
          />
          {withClearIcon && (
            <AppkitIcon
              className="clear-icon"
              icon="close"
              type="fill"
              size={8}
              onClick={handleClearClick}
            />
          )}
        </>
      )}
    </div>
  );
};

SelectEditorComponent.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
    PropTypes.arrayOf(PropTypes.string),
  ]),
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  })),
  config: PropTypes.shape({
    multi: PropTypes.bool,
    closeOnSelect: PropTypes.bool,
    filterOptions: PropTypes.bool,
    openOnFocus: PropTypes.bool,
    tabSelectsValue: PropTypes.bool,
    onSelectResetsInput: PropTypes.bool,
    inputProps: PropTypes.shape({
      className: PropTypes.string,
    }),
  }),
  withAllOption: PropTypes.bool,
  truncatable: PropTypes.bool,
  withClearIcon: PropTypes.bool,
  defaultEmpty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
};

SelectEditorComponent.defaultProps = {
  value: '',
  options: [],
  config: {},
  withAllOption: false,
  truncatable: false,
  withClearIcon: false,
  defaultEmpty: '',
};

class SelectCellEditor {
  init(params) {
    this.params = params;
    this.options = params.options || [];
    this.config = params.config || {};
    this.withAllOption = params.withAllOption;
    this.truncatable = params.truncatable;
    this.withClearIcon = params.withClearIcon;
    this.autoSaveEnabled = params.autoSaveEnabled;
    this.defaultEmpty = params.defaultEmpty;
    this.value = this.getInitialValue(params.value);
    this.eInput = document.createElement('div');
    this.eInput.style.width = '100%';
    this.eInput.style.height = '100%';
    this.root = createRoot(this.eInput);
    this.renderComponent();
  }

  getInitialValue(value) {
    if (!this.config.multi) {
      return value || '';
    }

    if (this.withAllOption && value && value[0] === ALL_OPTION) {
      return this.options;
    }

    if (!value) {
      return [];
    }

    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return value.map(val => this.options.find(option => option.value === val)).filter(Boolean);
    }

    if (Array.isArray(value)) {
      return value;
    }

    return [];
  }

  renderComponent() {
    flushSync(() => {
      this.root.render(
        React.createElement(SelectEditorComponent, {
          value: this.value,
          options: this.options,
          config: this.config,
          withAllOption: this.withAllOption,
          truncatable: this.truncatable,
          withClearIcon: this.withClearIcon,
          defaultEmpty: this.defaultEmpty,
          onChange: this.handleChange,
          onKeyDown: this.handleKeyDown,
        })
      );
    });
  }

  handleChange = params => {
    if (this.config && this.config.multi) {
      this.value = params ? params : [];
    } else {
      const newValue = params && typeof params === 'object' && typeof params.value !== 'undefined'
        ? params.value
        : params;
      this.value = newValue !== null ? newValue : '';
    }
    this.renderComponent();

    if (this.config && this.config.closeOnSelect && !this.config.multi) {
      if (this.params.api) {
        this.params.api.stopEditing();
      }
    }
  };

  handleKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.params.api) {
        this.params.api.stopEditing();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.params.api) {
        this.params.api.stopEditing(true);
      }
      return;
    }
  };

  getGui() {
    return this.eInput;
  }

  afterGuiAttached() {
    if (this.autoSaveEnabled) {
      this.selectCellEditorDropdown();
      this.setSelectedValue();
    }
  }

  getValue() {
    if (!this.config.multi) {
      return this.value;
    }

    if (this.withAllOption && this.value.length === this.options.length && this.value.every(
      item => this.options.some(option => option.value === (item.value || item))
    )) {
      return [ALL_OPTION];
    }

    return this.value.map(item => item.value || item);
  }

  isPopup() {
    return true;
  }

  isCancelBeforeStart() {
    return false;
  }

  isCancelAfterEnd() {
    return false;
  }

  destroy() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  selectCellEditorDropdown() {
    const elem = document.getElementsByClassName(`cell-editor-dropdown`)[0];
    if (!elem || !elem.children.length) {
      return;
    }
    const input = [...elem.children].find(child => child.nodeName === 'INPUT');
    if (!input) {
      return;
    }
    input.select();
  }

  setSelectedValue() {
    const selected = document.getElementsByClassName(`Select__option--is-focused`);
    if (!selected.length) {
      return;
    }
    const label = selected[0].querySelector('.Select__option-label');
    const title = label ? label.getAttribute('title') : selected[0].textContent;
    const foundValue = this.options.find(option => option.label === title);
    if (!foundValue) {
      return;
    }

    if (this.config.multi) {
      if (!this.value.some(item => (item.value || item) === foundValue.value)) {
        this.value = [...this.value, foundValue];
      }
    } else {
      this.value = foundValue.value;
    }

    this.renderComponent();
  }
}

export default SelectCellEditor;
